import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit
} from '@angular/core';
import {
  ModalController,
  NavController
} from '@ionic/angular';
import {TranslateService} from "@ngx-translate/core";
import {Poi} from "../../../../classes/common/poi";
import {AppComponent} from "../../../../app.component";
import {
  ViewService
} from "../../../../services/common/view.service";
import {
  PoiMapService,
  PolygonDrawState
} from "../../../../services/common/poi-map.service";
import {Subscription} from "rxjs";
import {
  ApiService
} from "../../../../services/common/api.service";
import {
  DataService
} from "../../../../services/common/data.service";
import {Utility} from "../../../../classes/common/utility";
import {
  AuthService
} from "../../../../services/common/auth.service";
import {DemoService} from "../../../../services/common/demo.service";
import {
  GestoreImmaginiService
} from "../../../../services/common/gestore-immagini.service";

@Component({
  selector: 'app-poi-add',
  templateUrl: './poi-add.page.html',
  styleUrls: ['./poi-add.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PoiAddPage implements OnInit {

  @Input() poi: Poi;
  @Input() lat: number;
  @Input() lng: any;

  @Input() appComponent: AppComponent;


  lang: any;

  listener = {
    view: null
  };

  addressError: string;
  address: string;
  searching: boolean;

  name: string;
  nameError; string;
  radius: number = 300;
  radiusError: number;
  labelNumber: number;
  labelNumberError: string;
  /** Colore area/bordo: default blu storico ma editabile dall'utente. */
  color: string = '#2e97f2';

  // ── Impostazioni punto di consegna (sempre visibili) ──
  readonly DELIVERY_DAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
  codiceCliente = '';
  deliveryDays: number[] = [];
  deliveryWindows: string[][] = [];
  poiNote = '';

  // page is for creating a new poi
  pageIsAdd: boolean;
  image_to_save: string;
  in_mod_image: boolean;

  /** Modalita' di creazione del POI:
   *  - 'circle' (default, legacy): POI cerchio con centro + raggio (com'è oggi).
   *  - 'avanzata' (work in progress): POI con area poligonale; il marker andra' al
   *    centroide. Per ora la modalita' avanzata e' solo placeholder, il flow di
   *    disegno e il salvataggio verranno fatti negli step successivi. */
  mode: 'circle' | 'avanzata' = 'circle';

  /** Wizard a 2 step (Variante B handoff):
   *  - 1: scelta del tipo di area (Cerchio / Avanzata) con tile grandi.
   *  - 2: configurazione (form + mappa) e salvataggio.
   *  In modifica si parte direttamente da 2 (la modalita' e' gia' decisa). */
  pageContent: number = 1;
  steps: string[] = ['POI_ADD.step1', 'POI_ADD.step2'];

  /* La mappa Google viene inizializzata SOLO quando il div #poi-map e' nel DOM
   * (cioe' a step 2). In step 1 il div non esiste -> initMap fallirebbe. */
  private mapInitialized: boolean = false;
  private pendingMapInit: {lat: number; lng: number} = null;

  /** Stato del disegno poligono (POI avanzato), aggiornato dal PoiMapService via Subject. */
  polyState: PolygonDrawState = { pointsCount: 0, closed: false, centroid: null, centroidValid: null };
  private polyStateSub: Subscription;

  constructor(
    private cd: ChangeDetectorRef,
    private modalCtrl: ModalController,
    public view: ViewService,
    private translate: TranslateService,
    public map: PoiMapService,
    private api: ApiService,
    private data: DataService,
    private navCtrl: NavController
  ) {}

  ngOnInit() {
    if(this.poi){
      this.labelNumber = this.poi.label_number;
      this.name = this.poi.name;
      this.radius = this.poi.radius;
      /* Colore area: pre-popolato col valore salvato (default blu storico se non c'e'). */
      if(this.poi.color) this.color = this.poi.color;
      /* Impostazioni punto di consegna (accordion): precarico i valori esistenti. */
      this.codiceCliente = this.poi.codice_cliente || '';
      this.deliveryDays = [...(this.poi.giorni_consegna || [])];
      this.deliveryWindows = (this.poi.orari_consegna || []).map((w) => [...w]);
      this.poiNote = this.poi.note || '';
      /* In modifica imposto subito il mode dal modello (default 'circle'). */
      this.mode = (this.poi.area_type === 'polygon') ? 'avanzata' : 'circle';
      /* Per i POI poligonali esistenti pre-imposto polyState come "chiuso" cosi'
       * il bottone Salva e' abilitato fin da subito (la mappa caricera' il
       * polygon dopo via syncMapMode, ma intanto l'utente puo' gia' modificare
       * nome/etichetta). */
      if(this.mode === 'avanzata' && this.poi.isPolygon && this.poi.isPolygon()){
        const verts = this.poi.getPolygonVertices();
        this.polyState = {
          pointsCount: verts.length,
          closed: true,
          centroid: null,
          centroidValid: null
        };
      }
      /* In modifica salto direttamente al passo 2 (la modalita' del POI esiste gia'). */
      this.pageContent = 2;
      ViewService.updateView.next(undefined);
    } else {
      this.pageIsAdd = true;
    }
  }

  async ionViewWillEnter(){
    if(!AuthService.isLogged && !DemoService.active) this.navCtrl.navigateRoot('login');
    if(this.appComponent) return await this.appComponent.checkPermission('poi', async () => {await this.dismiss()});
  }

  ionViewDidEnter() {
    this.listenerView();

    /* Subscribe allo stato poligono: ogni cambio (vertici, chiusura, edit) emette
     * un nuovo stato che la UI rispecchia (counter, abilitazione bottoni, salva). */
    this.polyStateSub = this.map.polygonState$.subscribe((s: PolygonDrawState) => {
      this.polyState = s;
      ViewService.updateView.next(undefined);
    });

    this.translate.get('POI_ADD').subscribe((lang: any) => {
      this.lang = lang;

      /* Passo i tooltip tradotti al PoiMapService (li usa per i title dei marker
       * poligono — pallino rosso "elimina area" e pallini midpoint). */
      this.map.setPolygonLang({
        cancellaArea: lang.tooltip_cancella_area,
        inserisciVertice: lang.tooltip_inserisci_vertice
      });

      /* Calcolo coordinate iniziali (priorita': POI esistente -> coord passate -> default). */
      let coordinates: {lat: number; lng: number} = {
        lat: 41.8910091,
        lng: 12.49207
      };
      if(this.poi && this.poi.position && this.poi.position.coordinates){
        coordinates.lat = this.poi.position.coordinates[1];
        coordinates.lng = this.poi.position.coordinates[0];
      }else if(this.lat && this.lng){
        coordinates.lat = this.lat;
        coordinates.lng = this.lng;
      }
      this.pendingMapInit = coordinates;

      /* Label number: in creazione propongo max+1. */
      if(this.data.pois){
        let max: number = 0;
        for(let poi of this.data.pois){
          if(poi.label_number >= max){
            max = poi.label_number;
          }
        }
        if (this.pageIsAdd) {
          this.labelNumber = max + 1;
        }
        ViewService.updateView.next(undefined);
      }

      /* Get address: API indipendente dalla mappa, la posso lanciare subito. */
      this.api.getAddress(this.data.selOrganization.me._id, coordinates.lat, coordinates.lng).then((address: any) => {
        this.address = address;
        ViewService.updateView.next(undefined);
      }).catch((err: any) => {
        console.error(err);
      });

      /* Init mappa: solo se siamo gia' a step 2 (caso modifica). In step 1 il div
       * #poi-map non esiste ancora, l'init avverra' al passaggio a step 2. */
      if(this.pageContent === 2){
        this.tryInitPoiMap();
      }

      ViewService.updateView.next(undefined);
    });
  }

  /** Inizializza la Google Map sul div #poi-map (prima volta) oppure ri-aggancia
   * il divMap se Angular ha distrutto/ricreato il container (es. tornando a step 1
   * e poi avanti a step 2). Idempotente. */
  private tryInitPoiMap(){
    if(!this.pendingMapInit || !this.lang) return;
    /* Aspetto un tick per essere sicuro che il *ngSwitchCase abbia montato il div. */
    setTimeout(() => {
      const el = document.getElementById('poi-map');
      if(!el) return;
      if(this.mapInitialized){
        /* La mappa esiste gia': basta ri-agganciare il divMap al nuovo container
         * (lo step 2 e' stato rimontato). Stato/marker/poligono preservati. */
        this.map.reattachToContainer();
        return;
      }
      this.map.initMap(
        'Google',
        this.lang.trascina_per_spostare,
        this.pendingMapInit.lat,
        this.pendingMapInit.lng,
        this.radius,
        this.poi?.image,
        this.labelNumber
      );
      this.mapInitialized = true;
      /* Applico il colore area corrente (pre-popolato da poi.color in edit, o
       * default blu in creazione). Va fatto subito dopo initMap cosi' il primo
       * render del cerchio standard usa gia' il colore giusto. */
      this.map.setAreaColor(this.color);
      /* Una volta inizializzata la mappa, allineo il mode (circle/avanzata)
       * con il flow corrispondente (poligono attivo o cerchio classico). */
      this.syncMapMode();
    }, 0);
  }

  /** Allinea la modalita' della mappa al this.mode corrente.
   * Chiamato dopo init mappa e ad ogni cambio mode mentre si e' a step 2. */
  private syncMapMode(){
    if(!this.mapInitialized) return;
    if(this.mode === 'avanzata'){
      /* Edit di un POI poligonale esistente: carico i vertici come stato "chiuso"
       * (identico a creazione dopo "Chiudi area": polygon pieno, pallini verdi
       * grandi draggabili, primo vertice rosso con X, midpoint cavi). */
      if(this.poi && this.poi.isPolygon && this.poi.isPolygon() && !this.advancedPoiLoaded){
        const verts = this.poi.getPolygonVertices();
        if(verts.length >= 3){
          this.map.loadPolygon(verts);
          this.advancedPoiLoaded = true;
          return;
        }
      }
      this.map.enterPolygonMode();
    }else{
      this.map.exitPolygonMode();
    }
  }

  /** Flag per non ricaricare i vertici del POI avanzato a ogni syncMapMode
   *  (es. dopo un toggle e ritorno). */
  private advancedPoiLoaded: boolean = false;

  ionViewWillLeave() {
    if (this.listener.view) {
      this.listener.view.unsubscribe();
    }
    if (this.polyStateSub) {
      this.polyStateSub.unsubscribe();
    }
    /* Pulizia: gli artifact del POI avanzato (polygon, vertici, midpoint, address
     * dot, ...) li aggiungiamo direttamente a Google Maps e non sono tracciati dal
     * GoogleMapsService.clearMap(). Se non li ripuliamo qui, restano disegnati
     * sulla mappa principale dopo la chiusura del modal. */
    this.map.cleanupPolygonArtifacts();
  }

  dismiss(data?: any) {
    this.modalCtrl.dismiss(data);
  }

  // ── Impostazioni punto di consegna (accordion) ──
  toggleDeliveryDay(i: number) {
    this.deliveryDays = this.deliveryDays.includes(i)
      ? this.deliveryDays.filter((d) => d !== i)
      : [...this.deliveryDays, i];
  }
  addWindow() { this.deliveryWindows = [...this.deliveryWindows, ['09:00', '18:00']]; }
  removeWindow(i: number) { this.deliveryWindows = this.deliveryWindows.filter((_, k) => k !== i); }
  setWindowFrom(i: number, v: string) { this.deliveryWindows = this.deliveryWindows.map((w, k) => k === i ? [v, w[1]] : w); }
  setWindowTo(i: number, v: string) { this.deliveryWindows = this.deliveryWindows.map((w, k) => k === i ? [w[0], v] : w); }

  /** Applica la modifica del POI (patchCreatePoi + campi consegna) e chiude con successo.
   *  `forceEdit` conferma la modifica anche se il POI è presente in una consegna. */
  /** Campi del P.O.I. nella forma in cui il server li ritorna: usati in demo per
   *  aggiornare/creare in locale al posto della chiamata API. */
  private buildPoiFields(posLat: number, posLng: number, url_image: string, polygonGeo: any): any {
    const fields: any = {
      name: this.name,
      label_number: this.labelNumber,
      position: {type: 'Point', coordinates: [posLng, posLat]},
      image: url_image,
      color: this.color || null
    };
    if(polygonGeo){
      fields.area_type = 'polygon';
      fields.area = polygonGeo;
      fields.radius = null;
    }else{
      fields.area_type = 'circle';
      fields.radius = this.radius;
      fields.area = null;
    }
    return fields;
  }

  private async finalizeEditPoi(posLat: number, posLng: number, url_image: string, polygonGeo: any, forceEdit: boolean): Promise<void> {
    let poi = DemoService.active
      ? this.data.updateDemoPoi(this.poi._id, this.buildPoiFields(posLat, posLng, url_image, polygonGeo))
      : await this.api.patchCreatePoi(this.data.selOrganization.me._id, this.poi._id, posLat, posLng, this.name, this.labelNumber, this.radius, url_image, polygonGeo, this.color, forceEdit);
    this.poi.setData(poi);
    const consegna = await this.saveConsegneFields();
    if (consegna) { this.poi.setData(consegna); poi = consegna; }
    await this.view.hideLoading();
    this.view.modalAlert(this.lang.successo, this.lang.poi_modificato, 'succ', this.lang.ok);
    this.dismiss({poi});
  }

  /** Salva i campi consegna (codice identificativo/orari/note) sul POI appena creato/modificato. */
  private async saveConsegneFields(): Promise<any> {
    if (!this.poi || !this.poi._id) return null;
    if(DemoService.active){
      return this.data.updateDemoPoi(this.poi._id, {
        codice_cliente: this.codiceCliente || '',
        note: this.poiNote || '',
        giorni_consegna: this.deliveryDays,
        orari_consegna: this.deliveryWindows,
      });
    }
    return this.api.patchPoiConsegna(this.data.selOrganization.me._id, this.poi._id, {
      codice_cliente: this.codiceCliente || '',
      note: this.poiNote || '',
      giorni_consegna: this.deliveryDays,
      orari_consegna: this.deliveryWindows,
    });
  }

  /** Imposta la modalita' (chiamato dalle tile dello step 1).
   * In avanzata nascondo il cerchio sulla mappa (il flow di disegno arrivera' negli
   * step successivi). */
  setMode(mode: 'circle' | 'avanzata'){
    if(this.mode === mode) return;
    this.mode = mode;
    /* Se sono gia' a step 2 la mappa esiste: switch immediato del flow. */
    this.syncMapMode();
    ViewService.updateView.next(undefined);
  }

  /* ─── Handlers PolyTools (binding sui bottoni laterali in modalita' avanzata) ─── */
  ptRemoveLast(){ this.map.removeLastPoint(); }
  ptClear(){ this.map.clearPolygon(); }
  ptClose(){ this.map.closePolygon(); }
  ptReopen(){ this.map.reopenPolygon(); }

  /** Live preview del colore area: ad ogni change applico sulla mappa. */
  onColorChange(){
    this.map.setAreaColor(this.color);
  }

  /** Naviga tra gli step del wizard.
   *  - step 1, delta -1 -> chiudi (Annulla).
   *  - step 1, delta +1 -> vai a step 2 (Procedi).
   *  - step 2, delta -1 -> torna a step 1 (Indietro) — solo in creazione, in modifica chiude.
   *  - step 2, delta +1 -> salva (creaPoi). */
  changePageContent(delta: number){
    const next = this.pageContent + delta;
    if(next < 1){
      this.dismiss();
      return;
    }
    if(next > 2){
      this.creaPoi();
      return;
    }
    /* In modifica lo step 1 non e' raggiungibile: indietro = dismiss. */
    if(next === 1 && !this.pageIsAdd){
      this.dismiss();
      return;
    }
    this.pageContent = next;
    ViewService.updateView.next(undefined);

    /* Al passaggio a step 2 il div #poi-map entra nel DOM: inizializzo la mappa
     * (idempotente — se gia' fatto, no-op). */
    if(this.pageContent === 2){
      this.tryInitPoiMap();
    }
  }

  listenerView() {
    this.listener.view = ViewService.updateView$.subscribe((obj?: any) => {
      this.view.pipeChanged++;
      this.cd.markForCheck();
      setTimeout(() => {
        this.cd.markForCheck();
      }, 250);
    });
  }

  // Ricerca l'indirizzo e posiziona il marker su di esso
  async searchAddress(){
    if(!this.address){
      this.addressError = this.lang.inserire_indirizzo;
      ViewService.updateView.next(undefined);
      return;
    }
    this.addressError = undefined;
    /* In demo il geocoding non è raggiungibile: mostro il messaggio inline
     * sotto l'input, come per un indirizzo non trovato. */
    if(DemoService.active){
      this.addressError = this.lang.demo_indirizzo_non_disponibile;
      ViewService.updateView.next(undefined);
      return;
    }
    this.searching = true;
    ViewService.updateView.next(undefined);
    try{
      let coordinates: any = await this.api.getCoordinates(this.data.selOrganization.me._id, this.address);
      if(!coordinates || !coordinates.lat || !coordinates.lon){
        throw new Error();
      }else{
        this.searching = false;
        // metto il marker nella posizione selezionata
        this.map.movePoiMarker(coordinates.lat, coordinates.lon);
      }
    }catch(err){
      console.error(err);
      this.searching = false;
      this.addressError = this.lang.impossibile_trovare_indirizzo;
    }
    ViewService.updateView.next(undefined);
  }

  // Effettua la verifica sul form
  async checkFormError(input: string, noTimeout?: boolean){
    if(!noTimeout) await Utility.wait(500);
    switch (input) {
      case 'name':
        if(!this.name){
          this.nameError = this.lang.inserisci_nome;
          break;
        }
        this.nameError = undefined;
        break;
      case 'radius':
        if(!this.radius || this.radius <= 0){
          this.radiusError = this.lang.raggio_invalido;
          break;
        }
        this.map.changeCircleRadius(this.radius);
        this.radiusError = undefined;
        break;
      case 'labelNumber':
        if(!this.labelNumber){
          this.labelNumberError = this.lang.inserisci_label_number;
          break;
        }
        if(this.labelNumber <= 0 || this.labelNumber > 999){
          this.labelNumberError = this.lang.label_number_invalido;
          break;
        }
        this.labelNumberError = undefined;
        break;
    }
    ViewService.updateView.next(undefined);
  }

  // Apre l'info window
  openInfoField(field: string){
    switch (field) {
      case 'label_number':
        return (this.view.modalAlert(
          this.lang.info,
          this.lang.info_etichetta_numerica,
          "info",
          this.lang.ok
        ));
    }
  }

  creaPoi(){
    this.checkFormError('name', true);
    /* In modalita' avanzata il raggio non si valida (non esiste). */
    if(this.mode === 'circle'){
      this.checkFormError('radius', true);
    }
    this.checkFormError('labelNumber', true);
    if(this.nameError || (this.mode === 'circle' && this.radiusError) || this.labelNumberError){
      return;
    }
    /* In modalita' avanzata serve almeno una geometria valida (>=3 vertici).
     * Se il poligono e' aperto (es. edit auto-reopen) lo chiudo prima del save
     * cosi' getGeoJsonPolygon torna il GeoJSON. */
    if(this.mode === 'avanzata'){
      if(this.polyState.pointsCount < 3){
        return;
      }
      if(!this.polyState.closed){
        this.map.closePolygon();
      }
    }
    // fare controllo numero poi esistente
    let poi: Poi;
    if(this.data.pois){
      poi = this.data.pois.find((elem: Poi) => {
        return (
          elem.label_number === this.labelNumber &&
          elem._id !== this.poi._id
        );
      });
    }
    if(poi){
      let buttons: any[] = [
        {
          text: this.lang.annulla,
          role: 'cancel'
        },
        {
          text: this.lang.conferma,
          handler: async () => {
            this.ausCreatePoi();
          }
        }
      ];
      this.view.presentAlert(this.lang.conferma, this.lang.esiste_label_number, buttons, `${this.lang.nome_poi}: ${poi.name}`);
    }else{
      this.ausCreatePoi();
    }
  }

  // Metodo ausiliario che distingue se patch o post effettua l'api di creazione di un poi, utile per la conferma del label_number già esistente
  private async ausCreatePoi(){

    /* Calcolo posizione + payload poligono in base alla modalita'. */
    let posLat: number, posLng: number;
    let polygonGeo: any = null;
    if(this.mode === 'avanzata'){
      const c = this.map.getCurrentCentroid();
      const poly = this.map.getGeoJsonPolygon();
      if(!c || !poly){
        this.view.modalAlert(this.lang.errori.errore, this.lang.errori.creazione_poi, 'warn', this.lang.ok);
        return;
      }
      posLat = c.lat;
      posLng = c.lng;
      polygonGeo = poly;
    } else {
      const position: any = this.map.poiMarker.position;
      // AdvancedMarkerElement.position puo' essere LatLng (con metodi) o LatLngLiteral.
      posLat = typeof position.lat === 'function' ? position.lat() : position.lat;
      posLng = typeof position.lng === 'function' ? position.lng() : position.lng;
    }

    if(this.poi){
      try{
        await this.view.showLoading();
        let url_image: string = this.poi.image;
        try{
          if(this.image_to_save){
            // Salviamo l'immagine su firebase e salviamo l'url sul participant
            url_image = await GestoreImmaginiService.uploadFileMarker(Utility.base64ToBlob(this.image_to_save), this.poi);
          }else if(this.image_to_save === null){
            url_image = null;
          }
        }catch(err){
          console.error(err);
          await this.view.modalAlert(this.lang.errori.errore, this.lang.errori.impossibile_salvare_immagine, 'warn', this.lang.ok);
        }
        try{
          await this.finalizeEditPoi(posLat, posLng, url_image, polygonGeo, false);
        }catch(err){
          if(!ApiService.isPoiInDeliveryEditError(err)) throw err;
          // il POI è in una consegna: chiedo conferma e, se accettata, riprovo con force_edit
          await this.view.hideLoading();
          this.view.modalAlert(this.lang.richiesta_conferma, ApiService.poiInDeliveryMessage(err), 'warn', [
            { label: this.lang.annulla, fill: 'outline', color: 'mid-light_base' },
            { label: this.lang.conferma, click: async () => {
              try{
                await this.view.showLoading();
                await this.finalizeEditPoi(posLat, posLng, url_image, polygonGeo, true);
              }catch(e){
                console.error(e);
                this.view.hideLoading();
                this.view.modalAlert(this.lang.errori.errore, this.lang.errori.modifica_poi, 'warn', this.lang.ok);
              }
            }},
          ]);
        }
      }catch(err){
        console.error(err);
        this.view.hideLoading();
        this.view.modalAlert(this.lang.errori.errore, this.lang.errori.modifica_poi, 'warn', this.lang.ok);
      }
    }else{
      try{
        await this.view.showLoading();
        let poi: any;
        let url_image: string;

        if(DemoService.active){
          /* In demo niente upload su firebase storage (non inizializzato senza
           * login): teniamo l'immagine come data url, si visualizza comunque. */
          url_image = this.image_to_save || null;
          poi = this.data.createDemoPoi(this.buildPoiFields(posLat, posLng, url_image, polygonGeo));
          this.poi = new Poi(poi);
        }else{
          poi = await this.api.postCreatePoi(this.data.selOrganization.me._id, posLat, posLng, this.name, this.labelNumber, this.radius, undefined, false, undefined, polygonGeo, this.color);
          this.poi = new Poi(poi);
          try{
            if(this.image_to_save){
              // Salviamo l'immagine su firebase e salviamo l'url sul participant
              url_image = await GestoreImmaginiService.uploadFileMarker(Utility.base64ToBlob(this.image_to_save), this.poi);
            }else if(this.image_to_save === null){
              url_image = null;
            }
          }catch(err){
            console.error(err);
            await this.view.modalAlert(this.lang.errori.errore, this.lang.errori.impossibile_salvare_immagine, 'warn', this.lang.ok);
          }
          poi = await this.api.patchCreatePoi(this.data.selOrganization.me._id, this.poi._id, posLat, posLng, this.name, this.labelNumber, this.radius, url_image, polygonGeo, this.color);
          this.poi.setData(poi);
        }
        const consegna = await this.saveConsegneFields();
        if (consegna) { this.poi.setData(consegna); poi = consegna; }

        await this.view.hideLoading();

        this.view.modalAlert(this.lang.successo, this.lang.poi_creato, 'succ', this.lang.ok);
        this.dismiss({poi});
      }catch(err){
        console.error(err);
        this.view.hideLoading();
        this.view.modalAlert(this.lang.errori.errore, this.lang.errori.creazione_poi, 'warn', this.lang.ok);
      }
    }

    this.data.pois = [...this.data.pois] //creo una copia dell'array per rendeere visibili le modifiche in virtual scroll
  }

  deletePoi() {
    // if page is not for creating a new POI
    if (!this.pageIsAdd) {
      let message: string;
      let button: any;
      message = this.lang.conferma_cancellazione_poi;
      button = {
        label: this.lang.conferma,
        click: async () => {
          await this.view.showLoading();
          try{
            if(DemoService.active){
              this.data.deleteDemoPoi(this.poi._id);
            }else{
              await this.api.deletePoi(this.data.selOrganization.me._id, this.poi._id);
            }
            this.view.modalAlert(this.lang.operazione_completata, this.lang.poi_eliminato, 'succ', this.lang.ok);
            this.dismiss();
            this.data.pois = [...this.data.pois]
          }catch (err) {
            console.error(err);
            const msg = ApiService.isPoiInDeliveryError(err)
              ? ApiService.poiInDeliveryMessage(err)
              : this.lang.errori.eliminazione_poi;
            this.view.modalAlert(this.lang.errore, msg, 'warn', this.lang.ok);
          }
          await this.view.hideLoading();
        }
      };
      // message += ` <b>` + (this.selectedCar.name || this.selectedCar.imei) + `</b>`;
      let buttons: any[] = [
        {
          label: this.lang.annulla,
          fill: 'outline',
          color: 'mid-light_base'
        },
        button
      ];
      this.view.modalAlert(this.lang.richiesta_conferma, message, 'warn', buttons);
    }
  }

  imageChanged(event: any){
    this.image_to_save = event;
    this.map.updateImage(this.image_to_save, this.labelNumber);
    ViewService.updateView.next(undefined);
  }

  inModificaImmagine(event: any){
    this.in_mod_image = event;
    ViewService.updateView.next(undefined);
  }

}
